import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import axios from "axios";
import { Upload, Link, X, Plus, Trash2, Grip, Info } from "lucide-react";

// Remove video imports and use static content instead
// These will be loaded from the public folder
const VIDEO_PATHS = {
  browseAndShortlist: "/browseandshortlist.mp4",
  requestInterview: "/requestinterview.mp4",
  waitForApps: "/waitforapps.mp4"
};

// A simple overlay for drag & drop feedback
const DragOverlay = ({ isVisible }) => (
  <div
    className={`fixed inset-0 bg-[#08f2c3]/10 pointer-events-none border-2 border-[#08f2c3] z-50
      transition-opacity duration-200 flex items-center justify-center
      ${isVisible ? "opacity-100" : "opacity-0"}`}
  >
    <div className={`transform transition-transform duration-200 ${isVisible ? "scale-100" : "scale-95"}`}>
      <div className="bg-white/90 rounded-lg shadow-lg p-6 text-center">
        <p className="text-xl font-medium text-gray-800">Drop your file here</p>
        <p className="text-sm text-gray-600 mt-2">Release to upload</p>
      </div>
    </div>
  </div>
);

const DragAndDropZone = ({ onFileSelect, isUploading, url, onClear }) => {
  const fileInputRef = useRef(null);
  const [isHovering, setIsHovering] = useState(false);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsHovering(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsHovering(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setIsHovering(false);
      if (e.dataTransfer.files?.length > 0) {
        onFileSelect(e.dataTransfer.files[0]);
      }
    },
    [onFileSelect],
  );

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileInput = (e) => {
    if (e.target.files?.length > 0) {
      onFileSelect(e.target.files[0]);
    }
  };

  return (
    <div
      onClick={handleClick}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={`relative border-2 border-dashed rounded-xl transition-all duration-200 cursor-pointer flex items-center justify-center
        ${isHovering ? "border-[#08f2c3] bg-[#08f2c3]/10" : "border-[#08f2c3] hover:bg-gray-50"}
        ${!url ? "min-h-[180px]" : "min-h-[80px]"}`}
    >
      <input ref={fileInputRef} type="file" accept=".pdf,.docx" onChange={handleFileInput} className="hidden" />
      {isUploading ? (
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#08f2c3]/10">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#08f2c3]" />
          </div>
        </div>
      ) : url ? (
        <div className="flex items-center space-x-3 w-full max-w-md p-4">
          <div className="bg-[#08f2c3]/10 rounded-lg p-3">
            <Upload size={24} className="text-[#08f2c3]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-700 truncate">{url.split("/").pop()}</p>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClear?.();
            }}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <X size={20} className="text-gray-400" />
          </button>
        </div>
      ) : (
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center w-full">
          <p className="text-xl font-bold text-gray-700 mb-2" style={{ fontFamily: "Poppins, sans-serif" }}>
            Upload your job description
          </p>
          <p className="text-sm text-gray-400">PDF, DOCX (max. 10MB)</p>
        </div>
      )}
    </div>
  );
};

// Enhanced tag input component that handles auto-focus and keyboard navigation
const EnhancedTagInput = ({ 
  value, 
  onChange, 
  onRemove, 
  onAdd, 
  placeholder, 
  index, 
  isLast, 
  isOnly,
  className = "" 
}) => {
  const inputRef = useRef(null);

  useEffect(() => {
    // Auto-focus the input if it's a newly added empty field
    if (isLast && !value && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isLast, value]);

  const handleKeyDown = (e) => {
    // Add new item when Enter is pressed and current field is not empty
    if (e.key === 'Enter' && value.trim()) {
      e.preventDefault();
      onAdd();
    } else if (e.key === 'Backspace' && !value && !isOnly) {
      // Remove current item when Backspace is pressed on an empty field (unless it's the only one)
      e.preventDefault();
      onRemove(index);
    }
  };

  return (
    <div className={`flex items-center gap-2 group transition-all duration-200 ${className}`}>
      <div className="flex-1 relative">
        <input
          ref={inputRef}
          type="text"
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(index, e.target.value)}
          onKeyDown={handleKeyDown}
          className="w-full py-2 px-3 rounded-lg border border-gray-200 text-sm text-gray-700 
            placeholder-gray-400 focus:outline-none focus:border-[#08f2c3] focus:ring-1 focus:ring-[#08f2c3]
            transition-colors"
        />
        {/* Simplified action buttons div */}
        <div className="absolute top-0 right-0 h-full flex items-center pr-3 gap-1">
          {/* Keep Trash (remove field) button, make it always visible if not the only field */}
          {!isOnly && (
            <button
              type="button"
              onClick={() => onRemove(index)}
              // Removed opacity classes to make it always visible when applicable
              className="text-gray-400 hover:text-red-500 p-1 rounded-full transition-colors flex items-center justify-center"
              aria-label="Remove field"
            >
              <Trash2 size={14} className="flex-shrink-0" />
            </button>
          )}
          
          {/* Keep Add button logic */}
          {isLast && value && (
            <button
              type="button"
              onClick={onAdd}
              className="text-gray-400 hover:text-gray-600 p-1 rounded-full transition-opacity opacity-0 group-hover:opacity-100 flex items-center justify-center" // Keep opacity for add, less critical
              aria-label="Add new field"
            >
              <Plus size={14} className="flex-shrink-0" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Enhanced tag display component with improved visibility and interaction
const EnhancedSuggestedTags = ({ title, tags, onAddTag, isLoading, usedTags }) => {
  // Split comma-separated tags into an array and filter out any already used tags
  const tagArray = tags 
    ? tags.split(',')
          .map(tag => tag.trim())
          .filter(tag => Boolean(tag) && !usedTags.includes(tag))
    : [];
  
  if (isLoading) {
    return (
      <div className="mt-2">
        <p className="text-xs text-gray-500 flex items-center">
          <span className="inline-block mr-2 w-4 h-4">
            <svg className="animate-spin h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </span>
          Analyzing job description...
        </p>
      </div>
    );
  }
  
  if (!tagArray.length) return null;
  
  return (
    <div className="mt-2">
      <p className="text-xs font-medium text-gray-600 mb-2">{title}</p>
      <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto pr-2">
        {tagArray.map((tag, index) => (
          <button
            key={index}
            onClick={() => onAddTag(tag)}
            className="px-3 py-1.5 bg-gray-100 hover:bg-[#08f2c3]/10 hover:border-[#08f2c3]/30 border border-gray-200 
              rounded-full text-xs text-gray-700 flex items-center gap-1 transition-all duration-200 
              transform hover:scale-105 active:scale-95"
          >
            <Plus size={12} className="text-[#08f2c3]" />
            {tag}
          </button>
        ))}
      </div>
    </div>
  );
};

// Small subtle badge to indicate internal use without being distracting
// Updated to include Info icon and custom tooltip
const InternalBadge = ({ className = "" }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipText = "These requirements are used internally by Jumpstart's AI to find the best candidate matches and are not shown to applicants.";

  return (
    <div 
      className={`relative inline-flex items-center ${className}`}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <span 
        className={`bg-blue-50 text-blue-700 text-xs px-2 py-1 rounded-full font-medium flex items-center gap-1 cursor-default`}
      >
        Internal Only
        <Info size={12} className="text-blue-500" />
      </span>
      
      {/* Custom Tooltip */}
      {showTooltip && (
        <div 
          className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-60 p-2 
                     bg-gray-800 text-white text-xs rounded-md shadow-lg z-10 
                     transition-opacity duration-200 opacity-100"
          role="tooltip"
        >
          {tooltipText}
          {/* Optional: Add a small arrow */}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 
                        border-l-4 border-l-transparent 
                        border-r-4 border-r-transparent 
                        border-t-4 border-t-gray-800"></div>
        </div>
      )}
    </div>
  );
};

// New Confirmation Modal Component
const UnsavedChangesModal = ({ isOpen, onConfirm, onCancel, title, children }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
      <div className="bg-white rounded-xl shadow-2xl p-6 sm:p-8 w-full max-w-md transform transition-all">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold text-gray-800">{title || "Confirm Navigation"}</h3>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 p-1 rounded-full"
            aria-label="Close modal"
          >
            <X size={20} />
          </button>
        </div>
        <div className="text-sm text-gray-600 mb-6">
          {children}
        </div>
        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            No, stay
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2.5 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors"
          >
            Yes, leave
          </button>
        </div>
      </div>
    </div>
  );
};

const JobDescription = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState("");
  const [roleId, setRoleId] = useState("");
  const [jobDescriptionUrl, setJobDescriptionUrl] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [keySkills, setKeySkills] = useState("");
  const [interviewProcess, setInterviewProcess] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showInterviewLink, setShowInterviewLink] = useState(false);
  const [interviewLink, setInterviewLink] = useState("");
  const [hiringPriorities, setHiringPriorities] = useState({
    mustHave: [""],
    niceToHave: [""]
  });
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResults, setAnalysisResults] = useState({
    must_have: "",
    nice_to_have: ""
  });
  const [suggestedMustHaveString, setSuggestedMustHaveString] = useState("");
  const [suggestedNiceToString, setSuggestedNiceToString] = useState("");
  
  // State for initial values to track unsaved changes
  const [initialJobDescriptionUrl, setInitialJobDescriptionUrl] = useState("");
  const [initialKeySkills, setInitialKeySkills] = useState("");
  const [initialInterviewProcess, setInitialInterviewProcess] = useState("");
  const [initialInterviewLink, setInitialInterviewLink] = useState("");
  const [initialHiringPriorities, setInitialHiringPriorities] = useState({
    mustHave: [""],
    niceToHave: [""]
  });
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  // Define steps for the progress bar
  const steps = [
    { step: 1, route: "/create-role/basic-info" },
    { step: 2, route: "/create-role/select-spotlight" },
    { step: 3, route: "/create-role/job-description" }
  ];

  const queryRoleId = searchParams.get("roleId") || localStorage.getItem("currentRoleId");

  // Clear handler for onClear
  const handleClearFile = () => {
    setJobDescriptionUrl("");
  };

  useEffect(() => {
    const handleDragEnter = (e) => {
      e.preventDefault();
      if (e.dataTransfer.types.includes("Files")) {
        setIsDraggingFile(true);
      }
    };
    const handleDragLeave = (e) => {
      e.preventDefault();
      if (!e.relatedTarget) {
        setIsDraggingFile(false);
      }
    };
    const handleDrop = (e) => {
      e.preventDefault();
      setIsDraggingFile(false);
    };

    window.addEventListener("dragenter", handleDragEnter);
    window.addEventListener("dragleave", handleDragLeave);
    window.addEventListener("drop", handleDrop);
    window.addEventListener("dragover", (e) => e.preventDefault());

    return () => {
      window.removeEventListener("dragenter", handleDragEnter);
      window.removeEventListener("dragleave", handleDragLeave);
      window.removeEventListener("drop", handleDrop);
      window.removeEventListener("dragover", (e) => e.preventDefault());
    };
  }, []);

  const location = useLocation();

  // Store the previous path when component mounts
  useEffect(() => {
    const previousPath = location.state?.from || "";
    localStorage.setItem("previousPath", previousPath);
  }, [location]);

  // Function to check for unsaved changes
  const hasUnsavedChanges = () => {
    if (jobDescriptionUrl !== initialJobDescriptionUrl) return true;
    if (keySkills !== initialKeySkills) return true;
    if (interviewProcess !== initialInterviewProcess) return true;
    if (interviewLink !== initialInterviewLink) return true;

    // Compare hiring priorities
    // Filter out empty strings from current priorities for comparison, 
    // as initial empty strings are typically represented as [""]
    const currentMustHave = hiringPriorities.mustHave.filter(Boolean);
    const currentNiceToHave = hiringPriorities.niceToHave.filter(Boolean);

    // Initial priorities might also just be [""] if nothing was loaded/entered
    const initialMustHave = initialHiringPriorities.mustHave.filter(Boolean);
    const initialNiceToHave = initialHiringPriorities.niceToHave.filter(Boolean);

    if (JSON.stringify(currentMustHave) !== JSON.stringify(initialMustHave)) return true;
    if (JSON.stringify(currentNiceToHave) !== JSON.stringify(initialNiceToHave)) return true;

    return false;
  };

  const handleBack = () => {
    if (hasUnsavedChanges()) {
      setIsConfirmModalOpen(true);
    } else {
      const previousPath = localStorage.getItem("previousPath");
      if (previousPath === "/spotlight") {
        navigate(`/create-role/spotlight?roleId=${roleId}`);
      } else {
        navigate(`/create-role/select-spotlight?roleId=${roleId}`);
      }
    }
  };

  const proceedWithNavigation = () => {
    const previousPath = localStorage.getItem("previousPath");
    if (previousPath === "/spotlight") {
      navigate(`/create-role/spotlight?roleId=${roleId}`);
    } else {
      navigate(`/create-role/select-spotlight?roleId=${roleId}`);
    }
  };

  const handleConfirmLeave = () => {
    setIsConfirmModalOpen(false);
    proceedWithNavigation();
  };

  const handleCancelLeave = () => {
    setIsConfirmModalOpen(false);
  };

  useEffect(() => {
    const storedToken = localStorage.getItem("token") || "";
    setToken(storedToken);
    setRoleId(queryRoleId || "");

    if (storedToken && queryRoleId) {
      localStorage.setItem("currentRoleId", queryRoleId);
      fetchRoleData(queryRoleId, storedToken);
    } else {
      // Set initial states if no data is fetched (e.g., new role)
      setInitialJobDescriptionUrl(jobDescriptionUrl);
      setInitialKeySkills(keySkills);
      setInitialInterviewProcess(interviewProcess);
      setInitialInterviewLink(interviewLink);
      setInitialHiringPriorities(JSON.parse(JSON.stringify(hiringPriorities))); // Deep copy
    }
  }, [queryRoleId]);

  const fetchRoleData = async (id, authToken) => {
    try {
      const { data } = await axios.get("/api/roles/fetchRoleById", {
        headers: { Authorization: `Bearer ${authToken}` },
        params: { id },
      });
      const fields = data.role.fields;
      setJobDescriptionUrl(fields["Job Description PDF"] || "");
      setKeySkills(fields["Specific Experience"] || "");
      setInterviewProcess(fields["Interview Process"] || "");
      setInterviewLink(fields["Hiring Lead Calendly Link"] || "");
      if (fields["Hiring Lead Calendly Link"]) {
        setShowInterviewLink(true);
      }
      
      // Load user-entered hiring priorities
      const mustHaveTagsString = fields["Must Haves"] || ""; 
      const niceToHaveTagsString = fields["Nice Haves"] || ""; 

      const parseTags = (tagsString) => {
        const tags = tagsString.split(',') // Split by comma
                               .map(tag => tag.trim()) // Trim whitespace
                               .filter(Boolean); // Remove any empty strings resulting from split/trim
        // If no tags were found after filtering, return [""], otherwise return the tags
        return tags.length > 0 ? tags : [""]; 
      };

      const loadedMustHave = parseTags(mustHaveTagsString);
      const loadedNiceToHave = parseTags(niceToHaveTagsString);

      // Set the state for the input fields
      setHiringPriorities({
        mustHave: loadedMustHave,
        niceToHave: loadedNiceToHave
      });

      // --- Load SAVED suggested tags from Airtable ---
      const fetchedSuggestedMust = fields["Suggested Must Haves"] || "";
      const fetchedSuggestedNice = fields["Suggested Nice Haves"] || "";
      setSuggestedMustHaveString(fetchedSuggestedMust);
      setSuggestedNiceToString(fetchedSuggestedNice);
      // --- End Load SAVED suggestions ---

      // Log loaded priorities for debugging
      if (loadedMustHave.some(Boolean) || loadedNiceToHave.some(Boolean)) {
        console.log("[JobDescription Page] Loaded existing hiring priorities from Airtable:", { mustHave: loadedMustHave.filter(Boolean), niceToHave: loadedNiceToHave.filter(Boolean) });
      }
      // --- End loading hiring priorities ---
      
      // Set initial values after fetching
      setInitialJobDescriptionUrl(fields["Job Description PDF"] || "");
      setInitialKeySkills(fields["Specific Experience"] || "");
      setInitialInterviewProcess(fields["Interview Process"] || "");
      setInitialInterviewLink(fields["Hiring Lead Calendly Link"] || "");
      setInitialHiringPriorities({ // Deep copy for initial state
        mustHave: [...loadedMustHave],
        niceToHave: [...loadedNiceToHave]
      });
      
    } catch (error) {
      console.error("Error fetching role data:", error);
    }
  };

  const handleFileUpload = async (file) => {
    if (!file) return;
    setIsUploading(true);
    try {
      const {
        data: { signature, timestamp },
      } = await axios.get("/api/cloudinarySignature", {
        params: { upload_preset: "ml_default" },
      });

      const formData = new FormData();
      formData.append("file", file);
      formData.append("upload_preset", "ml_default");
      formData.append("timestamp", timestamp);
      formData.append("api_key", "815772339983468");
      formData.append("signature", signature);
      formData.append("folder", "JobDescription");

      const response = await axios.post("https://api.cloudinary.com/v1_1/dkjsnpplv/raw/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const cloudinaryUrl = response.data.secure_url;
      setJobDescriptionUrl(cloudinaryUrl);

      // Immediately update the role with the new file URL
      await axios.patch(
        "/api/roles/updateRoleData",
        {
          fields: {
            "Job Description PDF": cloudinaryUrl,
          },
          roleId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      // Trigger job description analysis in the background.
      setIsAnalyzing(true);
      
      try {
        console.log("Starting JD analysis API call with roleId:", roleId);
        const analysisResponse = await axios.post(
          "/api/candidates/analyzeJobDescription",
          { roleId },
          {
            headers: { 
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json"
            },
          }
        );
        console.log("JD Analysis API response received:", analysisResponse.data);
        if (analysisResponse.data.jdAnalysis) {
          const { must_have, nice_to_have } = analysisResponse.data.jdAnalysis;
          setAnalysisResults({ must_have, nice_to_have });
          setSuggestedMustHaveString(must_have);
          setSuggestedNiceToString(nice_to_have);
          console.log("JD Analysis completed & suggestions updated:", { must_have, nice_to_have });
        } else {
          console.error("JD Analysis response missing jdAnalysis data:", analysisResponse.data);
        }
      } catch (error) {
        console.error("Error analyzing job description:", error);
        console.error("Error details:", error.response?.data || "No response data");
        console.error("Error status:", error.response?.status || "No status");
        console.error("Error URL:", "/api/candidates/analyzeJobDescription");
      } finally {
        setIsAnalyzing(false);
      }
    } catch (error) {
      console.error("Upload error:", error);
    } finally {
      setIsUploading(false);
    }
  };

  // Update function for Save & Continue (now saves tags as well)
  const updateRoleData = async () => {
    if (!roleId || !token) return Promise.resolve();
    try {
      // Convert the hiringPriorities arrays to comma-separated strings
      const mustHaveTags = hiringPriorities.mustHave.filter(Boolean).join(', ');
      const niceToHaveTags = hiringPriorities.niceToHave.filter(Boolean).join(', ');

      const response = await axios.patch(
        "/api/roles/updateRoleData",
        {
          fields: {
            "Job Description PDF": jobDescriptionUrl,
            "Specific Experience": keySkills,
            "Interview Process": interviewProcess,
            "Hiring Lead Calendly Link": showInterviewLink ? interviewLink : "",
            "Must Haves": mustHaveTags,
            "Nice Haves": niceToHaveTags
          },
          roleId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response;
    } catch (error) {
      console.error("Error updating role data:", error);
      // Add specific logging for Airtable field errors if possible
      if (error.response?.data?.error?.message?.includes('UNKNOWN_FIELD_NAME')) {
          console.error("Airtable Error: One of the fields (e.g., 'Must Haves', 'Nice Haves') might not exist or is misspelled in the base.");
      }
      return Promise.resolve(); // Allow navigation to continue despite error, but maybe add user feedback
    }
  };

  // Simplified handleSaveAndContinue - just save and navigate
  const handleSaveAndContinue = async () => {
    setIsLoading(true);
    try {
      // Save the other fields (JD URL, skills, process, link)
      await updateRoleData();
      setIsLoading(false);

      // Prepare tags to pass via navigation state
      const mustHaveTagsString = hiringPriorities.mustHave.filter(Boolean).join(', ');
      const niceToHaveTagsString = hiringPriorities.niceToHave.filter(Boolean).join(', ');

      // Navigate directly to review page, passing tags in state
      navigate(`/create-role/review?roleId=${roleId}`, {
        state: {
          manualMustHaveTags: mustHaveTagsString,
          manualNiceToHaveTags: niceToHaveTagsString
        }
      });
    } catch (error) {
      console.error("Error updating role data:", error);
      setIsLoading(false);
      // Still navigate even if update fails, but maybe log the error more visibly?
      // Consider adding error handling/user feedback here
      navigate(`/create-role/review?roleId=${roleId}`);
    }
  };

  // Function to handle progress bar click navigation
  const handleProgressClick = (route) => {
    if (roleId) {
      navigate(`${route}?roleId=${roleId}`);
    }
  };

  // Function to handle saving draft
  const handleSaveDraft = async () => {
    try {
      await updateRoleData();
      // Show toast or notification here if needed
      console.log("Draft saved successfully");
    } catch (error) {
      console.error("Error saving draft:", error);
    }
  };

  // Function to handle skipping this step
  const handleSkip = () => {
    navigate(`/create-role/review?roleId=${roleId}`);
  };

  // Enhanced handlers for tag management
  const addTagField = (listType) => {
    const newPriorities = { ...hiringPriorities };
    newPriorities[listType].push("");
    setHiringPriorities(newPriorities);
  };

  const removeTagField = (listType, index) => {
    const newPriorities = { ...hiringPriorities };
    newPriorities[listType].splice(index, 1);
    setHiringPriorities(newPriorities);
  };

  const updateTagField = (listType, index, value) => {
    const newPriorities = { ...hiringPriorities };
    newPriorities[listType][index] = value;
    setHiringPriorities(newPriorities);
  };

  const addSuggestedTag = (listType, tag) => {
    // Find the first empty field or add to end
    const newPriorities = { ...hiringPriorities };
    const emptyIndex = newPriorities[listType].findIndex(item => item === "");
    
    if (emptyIndex >= 0) {
      newPriorities[listType][emptyIndex] = tag;
    } else {
      newPriorities[listType].push(tag);
    }
    
    setHiringPriorities(newPriorities);
  };

  return (
    <div className="h-screen flex flex-col bg-white">
      <DragOverlay isVisible={isDraggingFile} />
      
      {/* Header */}
      <div className="pt-4 pb-2 flex-shrink-0">
        <div className="w-full max-w-5xl mx-auto relative px-6">
          <h1 className="text-[32px] font-medium text-center">Create role</h1>
          <button
            onClick={handleSaveDraft}
            className="px-6 py-2 border border-gray-300 text-gray-600 rounded-full hover:bg-gray-50 transition-colors absolute right-6 top-1/2 -translate-y-1/2"
          >
            Save draft
          </button>
        </div>
        {/* Progress bar */}
        <div className="w-full max-w-5xl mx-auto mt-4 px-6">
          <div className="flex gap-2 cursor-pointer">
            {steps.map(({ step, route }) => (
              <div
                key={step}
                onClick={() => handleProgressClick(route)}
                className={`h-2 flex-1 rounded-full ${step <= 3 ? "bg-[#08f2c3]" : "bg-[#cbcbcb]"}`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Main content area */}
      <div className="flex-1 flex items-center justify-center">
        <div className="w-full max-w-4xl px-6">
          {/* Add top margin here to push content down */}
          <section className="space-y-6 mt-8">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-2xl font-medium">Job Description</h2>
              <a
                href="https://jumpstart-alumni.notion.site/Writing-a-Job-Description-33504887841241d0a52f5a2a9e23e245"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium text-black hover:text-gray-600 underline underline-offset-4"
              >
                View templates &amp; guidance
              </a>
            </div>
            <div className="space-y-2">
              <DragAndDropZone
                onFileSelect={handleFileUpload}
                isUploading={isUploading}
                url={jobDescriptionUrl}
                onClear={handleClearFile}
              />
              <div className="relative mt-4">
                <input
                  type="text"
                  placeholder="Or share a link to your job description"
                  className="w-full p-4 pl-11 rounded-xl border border-gray-200 text-sm text-gray-700 
                    placeholder-gray-400 focus:outline-none focus:border-[#08f2c3] focus:ring-1 focus:ring-[#08f2c3]
                    hover:border-gray-300 transition-colors"
                  value={jobDescriptionUrl}
                  onChange={async (e) => {
                    const newUrl = e.target.value;
                    setJobDescriptionUrl(newUrl);
                    if (newUrl && (newUrl.startsWith("http://") || newUrl.startsWith("https://"))) {
                      try {
                        await axios.patch(
                          "/api/roles/updateRoleData",
                          {
                            fields: {
                              "Job Description PDF": newUrl
                            },
                            roleId,
                          },
                          {
                            headers: {
                              Authorization: `Bearer ${token}`,
                              "Content-Type": "application/json",
                            },
                          }
                        );
                        setIsAnalyzing(true);
                        
                        console.log("Starting JD analysis API call from URL input with roleId:", roleId);
                        const analysisResponse = await axios.post(
                          "/api/candidates/analyzeJobDescription",
                          { roleId },
                          {
                            headers: { 
                              Authorization: `Bearer ${token}`,
                              "Content-Type": "application/json"
                            },
                          }
                        );
                        console.log("JD Analysis API response from URL input received:", analysisResponse.data);
                        if (analysisResponse.data.jdAnalysis) {
                          const { must_have, nice_to_have } = analysisResponse.data.jdAnalysis;
                          setAnalysisResults({ must_have, nice_to_have });
                          setSuggestedMustHaveString(must_have);
                          setSuggestedNiceToString(nice_to_have);
                          console.log("JD Analysis completed & suggestions updated:", { must_have, nice_to_have });
                        } else {
                          console.error("JD Analysis response missing jdAnalysis data:", analysisResponse.data);
                        }
                      } catch (error) {
                        console.error("Error analyzing job description:", error);
                        console.error("Error details:", error.response?.data || "No response data");
                        console.error("Error status:", error.response?.status || "No status");
                        console.error("Error URL:", "/api/candidates/analyzeJobDescription");
                      } finally {
                        setIsAnalyzing(false);
                      }
                    }
                  }}
                />
                <Link className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              </div>
            </div>
            {/* Form Fields */}
            <div className="mt-8 space-y-8">
              <div className="transition-all duration-200 hover:bg-gray-50 rounded-lg p-4 -mx-4">
                <div className="mb-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-[15px] font-medium text-gray-800">Highlight key skills to attract candidates</h3>
                    <span className="bg-green-50 text-green-700 text-xs px-2 py-1 rounded-full font-medium">Public</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">This will appear on your job posting to help attract the right candidates</p>
                </div>
                <textarea
                  value={keySkills}
                  onChange={(e) => setKeySkills(e.target.value)}
                  placeholder="E.g., Looking for someone with React experience who thrives in fast-paced environments and values collaborative problem-solving."
                  className="w-full p-3 rounded-lg border border-gray-200 text-sm text-gray-700
                    placeholder-gray-400 focus:outline-none focus:border-[#08f2c3] focus:ring-1 focus:ring-[#08f2c3] transition-colors"
                  rows={3}
                />
              </div>
              <div className="rounded-xl border border-gray-200 hover:border-gray-300 transition-all duration-200 p-5 bg-white shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-[15px] font-medium text-gray-800">What matters most for this role?</h3>
                      <InternalBadge />
                    </div>
                    <p className="text-xs text-gray-500">
                      These priorities help us match the perfect candidates to your needs
                    </p>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  {/* Must-have requirements column */}
                  <div className="space-y-3">
                    <h4 className="text-sm font-medium text-gray-700 flex items-center">
                      <span className="w-1.5 h-1.5 bg-red-500 rounded-full mr-2"></span>
                      Must-have requirements
                    </h4>
                    
                    <div className="pl-3 border-l-2 border-red-100 space-y-1.5">
                      {hiringPriorities.mustHave.map((priority, index) => (
                        <EnhancedTagInput 
                          key={`must-${index}`}
                          value={priority}
                          onChange={(idx, val) => updateTagField('mustHave', idx, val)}
                          onRemove={() => removeTagField('mustHave', index)}
                          onAdd={() => addTagField('mustHave')}
                          placeholder={index === 0 ? "e.g., 3+ years Python experience" : "Add another must-have requirement"}
                          index={index}
                          isLast={index === hiringPriorities.mustHave.length - 1}
                          isOnly={hiringPriorities.mustHave.length === 1}
                        />
                      ))}
                    </div>
                    
                    <EnhancedSuggestedTags 
                      title="Suggested must-have requirements:" 
                      tags={suggestedMustHaveString}
                      isLoading={isAnalyzing}
                      usedTags={[...hiringPriorities.mustHave, ...hiringPriorities.niceToHave].filter(Boolean)}
                      onAddTag={(tag) => addSuggestedTag('mustHave', tag)}
                    />
                  </div>
                  
                  {/* Nice-to-have requirements column */}
                  <div className="space-y-3">
                    <h4 className="text-sm font-medium text-gray-700 flex items-center">
                      <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-2"></span>
                      Nice-to-have requirements
                    </h4>
                    
                    <div className="pl-3 border-l-2 border-blue-100 space-y-1.5">
                      {hiringPriorities.niceToHave.map((priority, index) => (
                        <EnhancedTagInput 
                          key={`nice-${index}`}
                          value={priority}
                          onChange={(idx, val) => updateTagField('niceToHave', idx, val)}
                          onRemove={() => removeTagField('niceToHave', index)}
                          onAdd={() => addTagField('niceToHave')}
                          placeholder={index === 0 ? "e.g., Experience with React" : "Add another nice-to-have requirement"}
                          index={index}
                          isLast={index === hiringPriorities.niceToHave.length - 1}
                          isOnly={hiringPriorities.niceToHave.length === 1}
                        />
                      ))}
                    </div>
                    
                    <EnhancedSuggestedTags 
                      title="Suggested nice-to-have requirements:" 
                      tags={suggestedNiceToString}
                      isLoading={isAnalyzing}
                      usedTags={[...hiringPriorities.mustHave, ...hiringPriorities.niceToHave].filter(Boolean)}
                      onAddTag={(tag) => addSuggestedTag('niceToHave', tag)}
                    />
                  </div>
                </div>
              </div>
              <div className="transition-all duration-200 hover:bg-gray-50 rounded-lg p-4 -mx-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-[15px] font-medium text-gray-800">Interview Process</h3>
                      <span className="text-xs text-gray-400">(Optional)</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Help candidates understand what to expect from your hiring process</p>
                  </div>
                  {!showInterviewLink ? (
                    <button
                      type="button"
                      onClick={() => setShowInterviewLink(true)}
                      className="text-xs text-[#08f2c3] font-medium hover:underline flex items-center self-start"
                    >
                      <Link size={14} className="mr-1" />
                      Add scheduling link
                    </button>
                  ) : null}
                </div>
                <div className="space-y-3">
                  <textarea
                    value={interviewProcess}
                    onChange={(e) => setInterviewProcess(e.target.value)}
                    placeholder="E.g., 30-min intro call → Technical assessment → Team interview → Final chat with CEO"
                    className="w-full p-3 rounded-lg border border-gray-200 text-sm text-gray-700
                      placeholder-gray-400 focus:outline-none focus:border-[#08f2c3] focus:ring-1 focus:ring-[#08f2c3] transition-colors"
                    rows={2}
                  />
                  {showInterviewLink && (
                    <div className="relative">
                      <input
                        type="text"
                        value={interviewLink}
                        onChange={(e) => setInterviewLink(e.target.value)}
                        placeholder="Add your Calendly or scheduling link"
                        className="w-full p-3 pl-9 pr-9 rounded-lg border border-gray-200 text-sm text-gray-700 
                          placeholder-gray-400 focus:outline-none focus:border-[#08f2c3] focus:ring-1 focus:ring-[#08f2c3]"
                      />
                      <Link className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                      <button
                        type="button"
                        onClick={() => {
                          setShowInterviewLink(false);
                          setInterviewLink("");
                        }}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
      <div className="sticky bottom-0 w-full bg-white border-t border-gray-200 mt-4 pt-2">
        <div className="w-full max-w-5xl mx-auto px-6 py-3 flex justify-between items-center">
          <button
            onClick={handleBack}
            className="px-6 py-2.5 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Back
          </button>
          <div className="flex gap-4">
            <button 
              onClick={handleSkip} 
              className="px-6 py-2.5 text-gray-500 hover:text-gray-700 transition-colors"
            >
              Skip for now
            </button>
            <button
              onClick={handleSaveAndContinue}
              disabled={isLoading}
              className="px-6 py-2.5 bg-[#08f2c3] text-black rounded-lg hover:bg-[#06c19c] 
                disabled:opacity-50 disabled:cursor-not-allowed transition-colors inline-flex items-center"
            >
              {isLoading ? (
                <span className="inline-flex items-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-4 w-4 text-black"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Saving...
                </span>
              ) : (
                <>
                  Save & Continue <span className="ml-1">→</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
      <UnsavedChangesModal
        isOpen={isConfirmModalOpen}
        onConfirm={handleConfirmLeave}
        onCancel={handleCancelLeave}
        title="Unsaved Changes"
      >
        <p>You have unsaved changes. Are you sure you want to leave? Your changes will be lost.</p>
      </UnsavedChangesModal>
    </div>
  );
};

export default JobDescription;
